import Base from '@studiometa/js-toolkit';
import anime from 'animejs';
import Cookies from 'js-cookie';

/**
 * @typedef {Object} LoaderRef
 * @property {HTMLElement} intro
 * @property {HTMLElement} message
 * @property {HTMLElement} exit
 * @property {HTMLElement[]} sentence[]
 */

/**
 * @typedef {Object} LoaderInterface
 * @property {LoaderRef} $refs
 */

/**
 * Homepage class.
 */
export default class Loader extends Base {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    name: 'Loader',
    refs: ['intro', 'message', 'exit', 'sentence[]'],
  };

  /**
   * Initialize the component's behavior.
   * @this {Loader & LoaderInterface}
   */
  mounted() {
    // Setup sentences.
    this.$refs.sentence.forEach((el) => {
      const content = el.innerHTML;
      el.innerHTML = `<span style="translateY(100%)">${content}</span>`;
    });

    // Trigger animation once ready.
    this.ready = document.readyState === 'complete';

    if (this.ready) {
      this.animate();
    } else {
      window.addEventListener('load', () => {
        this.animate();
      });
    }
  }

  /**
   * Animate the loader.
   * @this {Loader & LoaderInterface}
   * @return void
   */
  animate() {
    Cookies.set('show-loader', 'false');

    anime({
      targets: this.$refs.intro,
      translateY: '-100%',
      easing: 'easeInOutQuart',
      duration: 1600,
    });

    this.$refs.sentence
      .map((el) => el.querySelector('span'))
      .forEach((sentence, index) => {
        anime({
          targets: sentence,
          translateY: ['100%', '0%'],
          easing: 'easeOutQuart',
          duration: 1400 - index * 200,
          delay: 600 + index * 200,
        });
      });

    this.$refs.sentence.forEach((sentence, index) => {
      anime({
        targets: sentence,
        translateX: index === 0 ? ['8vw', '-2vw'] : ['-3vw', '5vw'],
        easing: 'easeOutQuart',
        duration: 4000,
      });
    });

    anime({
      targets: this.$refs.message,
      translateY: '-30vh',
      easing: 'easeInQuart',
      duration: 800,
      delay: 2600,
    });

    anime({
      targets: this.$refs.exit,
      translateY: ['100%', '0%'],
      easing: 'easeInQuart',
      duration: 1000,
      delay: 2400,
      complete: () => {
        this.$refs.intro.style.display = 'none';
        this.$refs.message.style.display = 'none';

        anime({
          targets: this.$refs.exit,
          translateY: ['0%', '-100%'],
          easing: 'easeOutQuart',
          duration: 1200,
          complete: () => {
            this.$el.style.display = 'none';
            document.body.classList.remove('with-loader');
          },
        });
      },
    });
  }

  /**
   * Unbind all events on destroy.
   *
   * @this {Loader & LoaderInterface}
   * @return {Loader} The Loader instance.
   */
  destroyed() {
    return this;
  }
}
